import Navigation from "@/components/Navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { Settings, Database, Download, AlertCircle, RefreshCw, FileText } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Link } from "wouter";

function CurrentVersionBadge() {
  const { data: latest, isLoading } = trpc.system.getLatestDeployment.useQuery();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 p-3 rounded-lg bg-secondary/50 border border-border">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
        <span className="text-sm text-muted-foreground">Loading version...</span>
      </div>
    );
  }

  if (!latest) {
    return (
      <div className="flex items-center justify-center gap-2 p-3 rounded-lg bg-secondary/50 border border-border">
        <span className="text-sm text-muted-foreground">No deployments logged yet</span>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between p-4 rounded-lg bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/30">
      <div>
        <div className="text-xs text-muted-foreground mb-1">Current Deployed Version</div>
        <div className="flex items-center gap-3">
          <span className="text-2xl font-bold text-purple-400">{latest.version}</span>
          <span className="text-sm text-muted-foreground">Fix #{latest.fixNumber}</span>
        </div>
      </div>
      <div className="text-right">
        <div className="text-xs text-muted-foreground">Last deployed</div>
        <div className="text-sm">
          {new Date(latest.deployedAt).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear());

  const utils = trpc.useUtils();
  const { data: logs, isLoading: logsLoading } = trpc.lottery.getScrapingLogs.useQuery({ limit: 10 });

  const scrapeYearMutation = trpc.lottery.scrapeYear.useMutation({
    onSuccess: (data) => {
      if (data.recordsScraped === 0) {
        toast.info(`Year ${data.year}: ${data.message}`);
      } else {
        toast.success(`Year ${data.year}: ${data.message}`);
      }
      utils.lottery.getScrapingLogs.invalidate();
      utils.lottery.getRecentDraws.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to scrape data");
      // Invalidate logs to show failed status
      utils.lottery.getScrapingLogs.invalidate();
    },
  });

  const scrapeAllMutation = trpc.lottery.scrapeAllYears.useMutation({
    onSuccess: (data) => {
      toast.success(`Scraped ${data.totalRecords} total records from all years!`);
      utils.lottery.getScrapingLogs.invalidate();
      utils.lottery.getRecentDraws.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to scrape all years");
    },
  });

  const updateLatestMutation = trpc.lottery.updateLatestDraws.useMutation({
    onSuccess: (data) => {
      if (data.newDraws > 0) {
        toast.success(`Added ${data.newDraws} new draw(s)!`);
      } else {
        toast.info(data.message);
      }
      utils.lottery.getScrapingLogs.invalidate();
      utils.lottery.getRecentDraws.invalidate();
      utils.lottery.getStatistics.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update latest draws");
    },
  });

  const clearStuckLogsMutation = trpc.lottery.clearStuckLogs.useMutation({
    onSuccess: (data) => {
      toast.success(data.message);
      utils.lottery.getScrapingLogs.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to clear logs");
    },
  });

  const clearFailedLogsMutation = trpc.lottery.clearFailedLogs.useMutation({
    onSuccess: (data) => {
      toast.success(data.message);
      utils.lottery.getScrapingLogs.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to clear logs");
    },
  });

  if (user?.role !== "admin") {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-background via-background to-blue-950/20">
        <Navigation />
        <main className="flex-1 flex items-center justify-center pb-20 md:pb-8">
          <Card className="max-w-md">
            <CardContent className="pt-6 text-center">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
              <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
              <p className="text-muted-foreground mb-4">
                You need admin privileges to access this page
              </p>
              <Link href="/">
                <Button>Go Home</Button>
              </Link>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-background via-background to-blue-950/20">
      <Navigation />
      <main className="flex-1 pb-20 md:pb-8">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-primary via-amber-300 to-primary bg-clip-text text-transparent">
                Admin Panel
              </h1>
              <p className="text-muted-foreground">
                Manage lottery data scraping and system operations
              </p>
            </div>

            {/* Deployment Log */}
            <Card className="bg-gradient-to-br from-card to-purple-950/20 border-purple-500/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-400" />
                  Deployment Log
                </CardTitle>
                <CardDescription>
                  View deployment history and track version releases
                </CardDescription>
              </CardHeader>
              <CardContent>
                <CurrentVersionBadge />
                <Link href="/admin/deployments" className="block mt-4">
                  <Button variant="outline" className="w-full">
                    <FileText className="w-4 h-4 mr-2" />
                    View Deployment History
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Quick Update - Check for New Draws */}
            <Card className="bg-gradient-to-br from-card to-blue-950/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-primary" />
                  Quick Update
                </CardTitle>
                <CardDescription>
                  Check for new lottery draws and update database (smart incremental update)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={() => updateLatestMutation.mutate()}
                  disabled={updateLatestMutation.isPending}
                  className="w-full"
                >
                  {updateLatestMutation.isPending ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Checking for updates...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Check for New Draws
                    </>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  This will only fetch new draws from the current year without re-scraping all historical data.
                </p>
              </CardContent>
            </Card>

            {/* Scrape All Years */}
            <Card className="bg-gradient-to-br from-card to-blue-950/20 border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="w-5 h-5 text-primary" />
                  Scrape All Historical Data
                </CardTitle>
                <CardDescription>
                  Import all lottery data from 2014 to present (this may take several minutes)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={() => scrapeAllMutation.mutate()}
                  disabled={scrapeAllMutation.isPending}
                  size="lg"
                  className="w-full"
                >
                  {scrapeAllMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                      Scraping all years...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Scrape All Years (2014 - {new Date().getFullYear()})
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Scrape Specific Year */}
            <Card className="bg-gradient-to-br from-card to-blue-950/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="w-5 h-5 text-primary" />
                  Scrape Specific Year
                </CardTitle>
                <CardDescription>
                  Import or update data for a specific year
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="year">Year</Label>
                  <Input
                    id="year"
                    type="number"
                    min={2014}
                    max={new Date().getFullYear()}
                    value={year}
                    onChange={(e) => setYear(parseInt(e.target.value))}
                  />
                </div>
                <Button
                  onClick={() => scrapeYearMutation.mutate({ year })}
                  disabled={scrapeYearMutation.isPending}
                  className="w-full"
                >
                  {scrapeYearMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                      Scraping year {year}...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Scrape Year {year}
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Scraping Logs */}
            <Card className="bg-gradient-to-br from-card to-blue-950/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-primary" />
                  Recent Scraping Operations
                </CardTitle>
                <CardDescription>History of data import operations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => clearStuckLogsMutation.mutate()}
                    disabled={clearStuckLogsMutation.isPending}
                    className="flex-1"
                  >
                    {clearStuckLogsMutation.isPending ? "Clearing..." : "Clear Stuck Logs"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => clearFailedLogsMutation.mutate()}
                    disabled={clearFailedLogsMutation.isPending}
                    className="flex-1"
                  >
                    {clearFailedLogsMutation.isPending ? "Clearing..." : "Clear Failed Logs"}
                  </Button>
                </div>
                {logsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : logs && logs.length > 0 ? (
                  <div className="space-y-3">
                    {logs.map((log) => (
                      <div
                        key={log.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-secondary/50 border border-border"
                      >
                        <div className="flex items-center gap-3">
                          <div className="text-sm font-semibold">Year {log.year}</div>
                          <div
                            className={`text-xs px-2 py-1 rounded-full ${
                              log.status === "completed"
                                ? "bg-green-500/20 text-green-400"
                                : log.status === "failed"
                                ? "bg-red-500/20 text-red-400"
                                : log.status === "in_progress"
                                ? "bg-blue-500/20 text-blue-400"
                                : "bg-gray-500/20 text-gray-400"
                            }`}
                          >
                            {log.status}
                          </div>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {log.recordsScraped || 0} records
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No scraping operations yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
